import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CalendarClock, CheckCircle2, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProjectDetails } from '@/types/project';
import { formatCurrency } from '@/components/Pages/ActiveProjects/projectFormatters';

export const BudgetTab: React.FC<{ project: ProjectDetails }> = ({ project }) => {
  return (
    <>
      {project.budget && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Budget Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium">Total Hours</p>
                  <p className="text-2xl font-bold">{project.budget.totalHours.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Used Hours</p>
                  <p className="text-2xl font-bold">{project.budget.usedHours.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Remaining Hours</p>
                  <p className="text-2xl font-bold">{project.budget.remainingHours.toLocaleString()}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Budget Utilization</span>
                  <span className="text-sm font-medium">
                    {Math.round((project.budget.usedHours / project.budget.totalHours) * 100)}%
                  </span>
                </div>
                <Progress 
                  value={(project.budget.usedHours / project.budget.totalHours) * 100}
                  className={cn(
                    "h-3",
                    (project.budget.usedHours / project.budget.totalHours) * 100 > 90 && "[&>div]:bg-error-500",
                    (project.budget.usedHours / project.budget.totalHours) * 100 > 75 && 
                    (project.budget.usedHours / project.budget.totalHours) * 100 <= 90 && "[&>div]:bg-warning-500"
                  )}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                  <span>{project.budget.usedHours.toLocaleString()}h used</span>
                  <span>{project.budget.remainingHours.toLocaleString()}h remaining</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scheduler Integration - Only show if scheduledHours exists */}
          {project.budget.scheduledHours !== undefined && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarClock className="h-5 w-5" />
                  Scheduler Integration
                </CardTitle>
                <CardDescription>
                  Future scheduled hours from Resource Scheduler
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg bg-info-50 dark:bg-info-950/20">
                    <p className="text-sm font-medium text-muted-foreground">Scheduled Hours</p>
                    <p className="text-3xl font-bold text-info-700 dark:text-info-400 mt-1">
                      {project.budget.scheduledHours.toLocaleString()}h
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Future allocated work
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg bg-success-50 dark:bg-success-950/20">
                    <p className="text-sm font-medium text-muted-foreground">Available Capacity</p>
                    <p className="text-3xl font-bold text-success-700 dark:text-success-400 mt-1">
                      {Math.max(0, project.budget.remainingHours - project.budget.scheduledHours).toLocaleString()}h
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Unallocated hours
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg bg-purple-50 dark:bg-purple-950/20">
                    <p className="text-sm font-medium text-muted-foreground">Capacity Used</p>
                    <p className="text-3xl font-bold text-purple-700 dark:text-purple-400 mt-1">
                      {Math.round((project.budget.scheduledHours / project.budget.remainingHours) * 100)}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Of remaining budget
                    </p>
                  </div>
                </div>

                {/* Scheduled Hours Progress Bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Scheduled Hours</span>
                    </div>
                    <span className="text-sm font-medium">
                      {Math.round((project.budget.scheduledHours / project.budget.remainingHours) * 100)}%
                    </span>
                  </div>
                  <Progress 
                    value={(project.budget.scheduledHours / project.budget.remainingHours) * 100}
                    className="h-3 [&>div]:bg-info-500"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                    <span>{project.budget.scheduledHours.toLocaleString()}h scheduled</span>
                    <span>{Math.max(0, project.budget.remainingHours - project.budget.scheduledHours).toLocaleString()}h available</span>
                  </div>
                </div>

                {/* Health Status */}
                <div className="flex items-center justify-center p-4 border rounded-lg bg-muted/30">
                  {(() => {
                    const scheduledPercentage = (project.budget.scheduledHours / project.budget.remainingHours) * 100;
                    if (scheduledPercentage < 60) {
                      return (
                        <div className="flex items-center gap-3 text-warning-700 dark:text-warning-400">
                          <TrendingDown className="h-6 w-6" />
                          <div>
                            <p className="font-semibold">Under-utilized</p>
                            <p className="text-sm opacity-90">Capacity available for additional work</p>
                          </div>
                        </div>
                      );
                    } else if (scheduledPercentage <= 90) {
                      return (
                        <div className="flex items-center gap-3 text-success-700 dark:text-success-400">
                          <CheckCircle2 className="h-6 w-6" />
                          <div>
                            <p className="font-semibold">On Track</p>
                            <p className="text-sm opacity-90">Well balanced resource allocation</p>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex items-center gap-3 text-error-700 dark:text-error-400">
                          <AlertCircle className="h-6 w-6" />
                          <div>
                            <p className="font-semibold">Over-scheduled</p>
                            <p className="text-sm opacity-90">At capacity risk - review commitments</p>
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {project.budget.totalCost && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Financial Budget</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium">Total Budget</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(project.budget.totalCost)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Used</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(project.budget.usedCost || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Remaining</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(project.budget.remainingCost || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {project.budget.roleBudgets && project.budget.roleBudgets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Resource Budget by Role</CardTitle>
                <CardDescription>Per-role hours and cost breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-medium">Role</th>
                        <th className="text-right py-2 px-2 font-medium">Budget Hours</th>
                        <th className="text-right py-2 px-2 font-medium">Actual Hours</th>
                        <th className="text-right py-2 px-2 font-medium">Remaining</th>
                        <th className="text-right py-2 pl-2 font-medium">% Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.budget.roleBudgets.map((role, index) => (
                        <tr key={index} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{role.roleName}</td>
                          <td className="text-right py-2 px-2">{role.budgetHours.toLocaleString()}</td>
                          <td className="text-right py-2 px-2">{role.actualHours.toLocaleString()}</td>
                          <td className="text-right py-2 px-2">{role.remainingHours.toLocaleString()}</td>
                          <td className="text-right py-2 pl-2">
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-xs",
                                role.percentUsed > 90 && "bg-error-500/10 text-error-700 dark:text-error-400",
                                role.percentUsed > 75 && role.percentUsed <= 90 && "bg-warning-500/10 text-warning-700 dark:text-warning-400"
                              )}
                            >
                              {Math.round(role.percentUsed)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </>
  );
};
